import { memo } from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import { USAGE_TYPE_TO_UI_LABEL } from "../utils/questionnaireManagementConstants";
import type {
  QuestionnaireCategoryFilter,
  QuestionnaireStatusFilter,
} from "../utils/questionnaireManagementFilterUtils";

export interface QuestionnaireManagementToolbarProps {
  searchQuery: string;
  statusFilter: QuestionnaireStatusFilter;
  categoryFilter: QuestionnaireCategoryFilter;
  onChangeSearchQuery: (value: string) => void;
  onChangeStatusFilter: (value: QuestionnaireStatusFilter) => void;
  onChangeCategoryFilter: (value: QuestionnaireCategoryFilter) => void;
  onCreateQuestionnaire: () => void;
}

const QuestionnaireManagementToolbar = memo(
  ({
    searchQuery,
    statusFilter,
    categoryFilter,
    onChangeSearchQuery,
    onChangeStatusFilter,
    onChangeCategoryFilter,
    onCreateQuestionnaire,
  }: QuestionnaireManagementToolbarProps) => {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            width: "100%",
            overflowX: "auto",
            pb: 0.5,
          }}
        >
          <TextField
            size="small"
            variant="filled"
            label="Buscar questionário"
            placeholder="Filtre por nome do questionário"
            value={searchQuery}
            onChange={(event) => onChangeSearchQuery(event.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            size="small"
            variant="filled"
            label="Status"
            value={statusFilter}
            onChange={(event) =>
              onChangeStatusFilter(event.target.value as QuestionnaireStatusFilter)
            }
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Ativo</MenuItem>
            <MenuItem value="draft">Rascunho</MenuItem>
            <MenuItem value="unknown">Em edição</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            variant="filled"
            label="Categoria"
            value={categoryFilter}
            onChange={(event) =>
              onChangeCategoryFilter(event.target.value as QuestionnaireCategoryFilter)
            }
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="triage">{USAGE_TYPE_TO_UI_LABEL.triage}</MenuItem>
            <MenuItem value="preConsultation">
              {USAGE_TYPE_TO_UI_LABEL.preConsultation}
            </MenuItem>
            <MenuItem value="postConsultation">
              {USAGE_TYPE_TO_UI_LABEL.postConsultation}
            </MenuItem>
            <MenuItem value="attendance">
              {USAGE_TYPE_TO_UI_LABEL.attendance}
            </MenuItem>
          </TextField>

          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            sx={{
              height: 36,
              px: 2,
              borderRadius: 2,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
            onClick={onCreateQuestionnaire}
          >
            Novo questionário
          </Button>
        </Stack>
      </Box>
    );
  },
);

QuestionnaireManagementToolbar.displayName = "QuestionnaireManagementToolbar";

export default QuestionnaireManagementToolbar;
